import { useParams, useLocation } from "react-router-dom";
import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import DashboardMap from "./DashboardMap";
import IndependentURLMap from "./MapForIndependentURL";
import LiveIcon from "../../images/dashboard/map_live.svg";
import "./Dashboard.css";
import { useTranslation } from "react-i18next";
import {
  getVehiclesByOrgIdIndependent,
  getRoutesByOrgIdIndependent,
  getStopsByOrgIdIndependent,
  handleApiError,
} from "./dashboardAPI";

const getVehiclesForRoutes = (allVehicles = [], routes = []) => {
  const routeIds = new Set(
    routes.map((route) => route?.id?.toString()).filter(Boolean),
  );
  const routeVehicleIds = new Set(
    routes.flatMap((route) =>
      (route?.vehicles || [])
        .map((vehicle) => vehicle?.device_id?.toString())
        .filter(Boolean),
    ),
  );

  return allVehicles.filter((vehicle) => {
    const deviceId = vehicle?.device_id?.toString();
    const routeId = vehicle?.routeId?.toString();

    return (
      (routeId && routeIds.has(routeId)) ||
      (deviceId && routeVehicleIds.has(deviceId))
    );
  });
};

export default function DashboardMapURL() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  // Pick whichever param name you prefer
  const orgId = queryParams.get("orgId") || queryParams.get("org");

  console.log("Org ID from query:", orgId);

  return <DashboardMapFilter orgId={orgId} />;
}

function DashboardMapFilter({ orgId, filterBy = "routes" }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  // const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [data, setData] = useState({
    vehicles: [],
    routes: [],
    stops: [],
    users: [],
  });

  // Filtered data state
  const [filteredStops, setFilteredStops] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);

  const handleItemChange = (event) => {
    const selectedValues = event.target.value;
    setSelectedItems(selectedValues);
    filterDataByItems(selectedValues);
  };

  const filterDataByItems = (selectedItemIds) => {
    if (selectedItemIds.length === 0) {
      // If no items selected, show all data
      setFilteredRoutes(data.routes);
      setFilteredStops(data.stops);
      setFilteredVehicles(data.vehicles);
      return;
    }

    if (filterBy === "routes") {
      // Existing route filtering logic
      const routes = data.routes.filter((route) =>
        selectedItemIds.includes(route.id.toString())
      );
      setFilteredRoutes(routes);

      const stops = data.stops.filter((stop) => {
        return selectedItemIds.some((routeId) => {
          const selectedRouteObj = data.routes.find(
            (r) => r.id.toString() === routeId
          );
          return selectedRouteObj?.routeCoordinates?.some(
            (coord) =>
              coord.latitude === stop.latitude &&
              coord.longitude === stop.longitude
          );
        });
      });
      setFilteredStops(stops);

      const vehicles = getVehiclesForRoutes(data.vehicles, routes);
      setFilteredVehicles(vehicles);
    } else if (filterBy === "stops") {
      // Filter by stops
      const stops = data.stops.filter((stop) =>
        selectedItemIds.includes(stop.id.toString())
      );
      setFilteredStops(stops);

      // Find routes that contain these stops
      const routes = data.routes.filter((route) => {
        return route.routeCoordinates?.some((coord) =>
          selectedItemIds.some((stopId) => {
            const selectedStop = data.stops.find(
              (s) => s.id.toString() === stopId
            );
            return (
              selectedStop &&
              coord.latitude === selectedStop.latitude &&
              coord.longitude === selectedStop.longitude
            );
          })
        );
      });
      setFilteredRoutes(routes);

      // Find vehicles on routes that contain these stops
      const vehicles = getVehiclesForRoutes(data.vehicles, routes);
      setFilteredVehicles(vehicles);
    } else if (filterBy === "vehicles") {
      // Filter by vehicles
      const vehicles = data.vehicles.filter((vehicle) =>
        selectedItemIds.includes(
          vehicle.id?.toString() || vehicle.device_id?.toString()
        )
      );
      setFilteredVehicles(vehicles);

      // Find routes for these vehicles
      const routeIds = vehicles
        .map((v) => v.routeId?.toString())
        .filter(Boolean);
      const routes = data.routes.filter((route) =>
        routeIds.includes(route.id.toString())
      );
      setFilteredRoutes(routes);

      // Find stops on these routes
      const stops = data.stops.filter((stop) => {
        return routes.some((route) =>
          route.routeCoordinates?.some(
            (coord) =>
              coord.latitude === stop.latitude &&
              coord.longitude === stop.longitude
          )
        );
      });
      setFilteredStops(stops);
    }
  };

  const getCurrentDataArray = () => {
    switch (filterBy) {
      case "stops":
        return data.stops;
      case "vehicles":
        return data.vehicles;
      case "routes":
      default:
        return data.routes;
    }
  };

  const getItemDisplayName = (item) => {
    switch (filterBy) {
      case "stops":
        return item.name || `Stop ${item.id}`;
      case "vehicles":
        return item.name || `Vehicle ${item.id || item.device_id}`;
      case "routes":
      default:
        return item.name || `Route ${item.id}`;
    }
  };

  const getItemId = (item) => {
    switch (filterBy) {
      case "vehicles":
        return (item.id || item.device_id)?.toString();
      default:
        return item.id?.toString();
    }
  };

  // Helper function to get placeholder text
  const getPlaceholderText = () => {
    switch (filterBy) {
      case "stops":
        return t("Select Stops");
      case "vehicles":
        return t("Select Vehicles");
      case "routes":
      default:
        return t("Select Routes");
    }
  };

  // Helper function to get selected count text
  const getSelectedCountText = (count) => {
    switch (filterBy) {
      case "stops":
        return t("{{count}} stops selected", { count });
      case "vehicles":
        return t("{{count}} vehicles selected", { count });
      case "routes":
      default:
        return t("{{count}} routes selected", { count });
    }
  };

  // Fetch initial data
  // Example usage in fetchInitialData
  const fetchInitialData = async () => {
    setIsLoading(true);
    try {
      const [vehicles, routes, stops] = await Promise.all([
        getVehiclesByOrgIdIndependent(orgId),
        getRoutesByOrgIdIndependent(orgId),
        getStopsByOrgIdIndependent(orgId),
      ]);

      setData({ vehicles, routes, stops, users: [] });
      setFilteredVehicles(vehicles);
      setFilteredRoutes(routes);
      setFilteredStops(stops);
    } catch (error) {
      handleApiError(error, "fetchInitialData (byOrgId)");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      {/* Header container with Live indicator and Select box */}
      <Box
        sx={{
          position: "absolute",
          top: 10,
          right: 20,
          left: 10,
          zIndex: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Live Map indicator */}
        <Box
          sx={{
            backgroundColor: "white",
            padding: "8px 16px",
            borderRadius: 1,
            boxShadow: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <img
            src={LiveIcon}
            alt="Live Map"
            style={{
              width: 20,
              height: 20,
              animation: "pulse 1.5s infinite",
            }}
          />
          <span style={{ fontSize: "12px" }}>{t("liveMap")}</span>
        </Box>
      </Box>

      {/* Map Container */}
      <Box sx={{ height: "100%", padding: "0px" }}>
        {isLoading ? (
          <div style={{ padding: "40px" }}>
            <div height={400} width="100%" />
          </div>
        ) : (
          <IndependentURLMap
            stops={filteredStops}
            routes={filteredRoutes}
            vehicles={filteredVehicles}
          />
        )}
      </Box>
    </Box>
  );
}


